import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NavIcon from './NavIcon';

const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { to: '/dashboard', icon: 'home', label: 'Dashboard' },
    ],
  },
  {
    label: 'Content',
    items: [
      { to: '/posts',        icon: 'edit',  label: 'Posts' },
      { to: '/brand-voice',  icon: 'spark', label: 'Brand Voice' },
      { to: '/brand-images', icon: 'image', label: 'Brand Images' },
    ],
  },
  {
    label: 'Engagement',
    items: [
      { to: '/escalations', icon: 'alert', label: 'Escalations', badgeKey: 'escalations' },
      { to: '/leads',       icon: 'star',  label: 'Leads',       badgeKey: 'leads' },
      { to: '/comments',    icon: 'chat',  label: 'Comments' },
      { to: '/messages',    icon: 'mail',  label: 'Messages' },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/audit',    icon: 'clipboard', label: 'Audit Log' },
      { to: '/settings', icon: 'settings',  label: 'Settings' },
    ],
  },
];

export default function AppShell({ children, badges = {} }) {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();

  // logout() calls hostedUILogout() which redirects the page — no navigate needed
  const handleLogout = () => { logout(); };

  const initials =
    [user?.firstName?.[0], user?.lastName?.[0]]
      .filter(Boolean)
      .join('')
      .toUpperCase() || (user?.email?.[0]?.toUpperCase() || '?');

  return (
    <div className="app-shell-with-sidebar enterprise-shell">
      <aside className={`sidebar enterprise-sidebar${collapsed ? ' sidebar--collapsed' : ''}`}>
        <div className="sidebar-header enterprise-sidebar-header">
          <div className="sidebar-logo">
            <div className="enterprise-logo-mark">H</div>
            {!collapsed && (
              <div className="sidebar-brand-text">
                <span className="sidebar-brand-name">HCA Marketing</span>
                <span className="sidebar-brand-sub">Admin Panel</span>
              </div>
            )}
          </div>
          <button
            className="sidebar-toggle-btn"
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? '›' : '‹'}
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Primary navigation">
          {NAV_SECTIONS.map(section => (
            <React.Fragment key={section.label}>
              {!collapsed && <div className="sidebar-section-label">{section.label}</div>}
              {collapsed && <div className="sidebar-section-divider" />}
              {section.items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `sidebar-nav-item${isActive ? ' sidebar-nav-item--active' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  <span className="sidebar-nav-icon">
                    <NavIcon name={item.icon} />
                  </span>
                  {!collapsed && <span className="sidebar-nav-label">{item.label}</span>}
                  {item.badgeKey && badges[item.badgeKey] > 0 && (
                    <span className="sidebar-badge">{badges[item.badgeKey]}</span>
                  )}
                </NavLink>
              ))}
            </React.Fragment>
          ))}
        </nav>

        <a
          href="https://notes.homecareofmi.com/admin"
          target="_blank"
          rel="noreferrer"
          className="sidebar-nav-item enterprise-external-link"
          title="Notes Admin"
        >
          <span className="sidebar-nav-icon">
            <NavIcon name="link" />
          </span>
          {!collapsed && <span className="sidebar-nav-label">Notes Admin</span>}
        </a>

        <div className="sidebar-spacer" />
        <div className="sidebar-footer">
          <div className="sidebar-user-row">
            <div className="sidebar-avatar">{initials}</div>
            {!collapsed && (
              <div className="sidebar-user-info">
                <div className="sidebar-user-name">{user?.email}</div>
                <div className="sidebar-user-role">Administrator</div>
              </div>
            )}
          </div>
          <button
            className={`sidebar-logout${collapsed ? ' sidebar-logout--icon' : ''}`}
            onClick={handleLogout}
            title="Sign out"
          >
            {collapsed ? '↩' : 'Sign out'}
          </button>
        </div>
      </aside>

      {/* ── Page content ── */}
      <main className="main-content enterprise-main">
        <div className="page-body">{children}</div>
      </main>
    </div>
  );
}
